import { motion } from 'framer-motion';
import { ShieldCheck, Building2, ChevronRight } from 'lucide-react';
import PortalShellV2 from '@/components/PortalShellV2';
import AutoCursor from '@/components/AutoCursor';

const tabs = ['Getting started', 'Prices', 'Standards', 'Reporting', 'Dictionary', 'Dev resources', 'Release management', 'Trainings'];

const cards = [
  { Icon: ShieldCheck, title: 'Shafafiya post office',        desc: 'Learn how Shafafiya works as a data integration platform for insurance providers', accent: 'bg-teal-600' },
  { Icon: ShieldCheck, title: 'Definitions',                  desc: 'Understand the basic terminologies used in the Shafafiya system',                accent: 'bg-teal-600' },
  { Icon: Building2,   title: 'How to use Shafafiya post office', desc: "Learn how to use Shafafiya as a 'post office' of data across health insurance systems", accent: 'bg-teal-600' },
];

export default function ReportPortalV2Scene({ onNext }) {
  // Cursor moves through the Resources page, then reveals the Report Issue
  // entry point (viewer is told Report Issue is in the portal navigation).
  const cursorPath = [
    { x: 560, y: 200, delay: 1.0 },
    { x: 560, y: 400, delay: 1.4 },
    { x: 560, y: 600, delay: 1.6 },
    { x: 560, y: 600, delay: 1.5, click: true },
  ];

  return (
    <PortalShellV2
      variant="sidebar"
      menu="lite"
      activeGroup="Resources"
      fullOverlay={<AutoCursor targets={cursorPath} startDelay={0.6} />}
    >
      <div data-testid="scene-report-portal-v2" className="px-10 pt-6 pb-8" onClick={onNext}>
        {/* tab row */}
        <div className="flex items-center justify-between border-b border-[#E5E7EB] mb-8">
          {tabs.map((t, i) => (
            <div
              key={t}
              className={`pb-3 px-2 text-[13px] font-body whitespace-nowrap cursor-pointer ${i === 0 ? 'text-[#0A0A0A] font-semibold border-b-2 border-[#0A0A0A] -mb-[2px]' : 'text-[#6B7280]'}`}
            >
              {t}
            </div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-7">
          <h1 className="font-heading font-extrabold text-[36px] leading-tight tracking-tight text-[#0A0A0A]">
            Getting Started with Shafafiya
          </h1>
          <p className="mt-1 font-body text-[13.5px] text-[#6B7280]">Here's all you need to know about how to get your platform started on Shafafiya</p>
        </motion.div>

        <div className="space-y-4">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.15 }}
              className="rounded-2xl border border-[#E5E7EB] bg-white px-6 py-5 flex items-center gap-5 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer"
            >
              <div className={`w-12 h-12 rounded-full ${c.accent}/10 border-2 border-teal-600/20 flex items-center justify-center flex-shrink-0`}>
                <c.Icon size={22} className="text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-[18px] text-[#0A0A0A] leading-snug mb-1">{c.title}</p>
                <p className="font-body text-[12.5px] text-[#6B7280]">{c.desc}</p>
                <p className="mt-1.5 text-[11px] font-body text-[#9CA3AF]">Last updated: 21 Dec' 25</p>
              </div>
              <ChevronRight size={18} className="text-[#9CA3AF] flex-shrink-0" />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0, x: [0, 6, 0, 6, 0] }}
          transition={{
            opacity: { delay: 1.2 },
            y: { delay: 1.2 },
            x: { duration: 1.4, delay: 2.2, repeat: Infinity, repeatType: 'loop' },
          }}
          className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-50 border border-teal-200 text-[12px] font-body text-teal-800"
        >
          <span className="w-2 h-2 rounded-full bg-teal-600 animate-pulse" />
          → Click anywhere to open the Report Issue flow
        </motion.div>
      </div>
    </PortalShellV2>
  );
}
